import { Building2, Users, TrendingUp, Award, Target, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { mockServices } from '../data/mock';

// Constants
const SERVICE_ICON_SIZE = 140;

const Services = () => {
  const iconMap = {
    'Building2': Building2,
    'Users': Users,
    'TrendingUp': TrendingUp,
    'Award': Award
  };

  const whyChooseUs = [
    {
      icon: Award,
      title: 'Industry Expertise',
      description: '56 years combined experience specialising exclusively in property and strata recruitment'
    },
    {
      icon: Target,
      title: 'Targeted Approach',
      description: 'We understand the unique requirements of each role and organisation'
    },
    {
      icon: Clock,
      title: 'Fast Turnaround',
      description: 'Average time-to-hire of 2-3 weeks for most positions'
    },
    {
      icon: Users,
      title: 'Extensive Network',
      description: 'Access to thousands of qualified property professionals across Australia'
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-50 via-white to-brand-50/50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block mb-6 px-6 py-3 bg-brand-400/10 rounded-full border border-brand-400/20">
            <p className="text-brand-400 font-bold text-lg">🎯 Specialist Recruitment Services</p>
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-8 tracking-tight leading-tight">
            Our <span className="text-brand-400">Services</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Specialised recruitment solutions for the property, real estate, and strata management sectors
          </p>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-24">
            {mockServices.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <div key={service.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="w-24 h-24 bg-brand-400/10 rounded-3xl flex items-center justify-center mb-8">
                      <Icon size={48} className="text-brand-400" />
                    </div>
                    <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-8 tracking-tight leading-tight">
                      {service.title}
                    </h2>
                    <p className="text-gray-600 text-lg mb-10 leading-relaxed">
                      {service.description}
                    </p>
                    <div className="space-y-5">
                      <h3 className="text-2xl font-black text-brand-400 mb-6 tracking-tight">Key Benefits:</h3>
                      {service.benefits.map((benefit) => (
                        <div key={benefit} className="flex items-start">
                          <div className="w-2.5 h-2.5 bg-brand-400 rounded-full mt-2.5 mr-5"></div>
                          <p className="text-gray-600 text-lg leading-relaxed">{benefit}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <Card className="bg-white border-2 border-gray-200 overflow-hidden hover:border-brand-400 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                      <CardContent className="p-0">
                        <div className="aspect-video bg-gradient-to-br from-brand-500/20 to-transparent flex items-center justify-center">
                          <Icon size={SERVICE_ICON_SIZE} className="text-brand-400/30" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6 tracking-tight leading-tight">
              Why Choose <span className="text-brand-400">Growth Talent Group</span>
            </h2>
            <p className="text-gray-600 text-xl font-medium leading-relaxed">
              What sets us apart in property recruitment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item) => (
              <Card key={item.title} className="bg-white border-2 border-gray-200 hover:border-brand-400 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-brand-400/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-brand-100 transition-all duration-300">
                    <item.icon size={36} className="text-brand-400" />
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-4 tracking-tight leading-tight">{item.title}</h3>
                  <p className="text-gray-600 text-base leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 tracking-tight">
              Our <span className="text-brand-400">Process</span>
            </h2>
            <p className="text-gray-600 text-lg">
              How we find the perfect match for your organisation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Understanding Your Needs',
                description: 'We conduct in-depth consultation to understand your organisation, culture, and specific requirements.'
              },
              {
                step: '02',
                title: 'Candidate Sourcing',
                description: 'Leveraging our extensive network and targeted recruitment strategies to identify top talent.'
              },
              {
                step: '03',
                title: 'Perfect Match',
                description: 'Rigorous screening and assessment to ensure candidates meet your technical and cultural requirements.'
              }
            ].map((item, index) => (
              <div key={item.step} className="relative">
                <div className="text-6xl font-bold text-brand-400/20 mb-4">{item.step}</div>
                <h3 className="text-2xl font-black text-gray-900 mb-4 tracking-tight">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
                {index < 2 && (
                  <div className="hidden md:block absolute top-8 -right-4 w-8 h-0.5 bg-brand-400/30"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
